import { enumMembers, type ApiSchema } from "../../api/apiSchema";
import { corpusReviewRequest } from "../../api/content/corpusReviewRequest";
import { readArray, readBoolean, readInteger, readNullableString, readObject, readOneOf, readString } from "../../api/http/jsonDecoding";

export type AnchorImpact = ApiSchema<"AnchorImpact">;
const IMPACTS = enumMembers<AnchorImpact>({ unchanged: true, moved: true, changed: true, removed: true });
type Decision = ApiSchema<"ImpactDecisionKind">;
const DECISIONS = enumMembers<Decision>({ carry_forward: true, re_review: true, re_run: true, withdraw: true, keep_historical: true });

export interface ImpactFinding {
  readonly module: string;
  readonly kind: string;
  readonly identifier: string;
  readonly anchor: string;
  readonly quote: string | null;
  readonly impact: AnchorImpact;
  readonly decision: Decision | null;
  readonly bulkCarryForwardEligible: boolean;
}

export interface ImpactReport {
  readonly head: string;
  readonly documentsChanged: number;
  readonly findings: readonly ImpactFinding[];
}

export function readImpactReport(value: unknown): ImpactReport {
  const body = readObject(value, "impact report");
  return {
    head: readString(body, "head"),
    documentsChanged: readInteger(body, "documents_changed"),
    findings: readArray(body["findings"], "impact findings", (value) => {
      const finding = readObject(value, "impact finding");
      return {
        module: readString(finding, "module"), kind: readString(finding, "kind"),
        identifier: readString(finding, "identifier"), anchor: readString(finding, "anchor"),
        quote: readNullableString(finding, "quote"), impact: readOneOf(finding, "impact", IMPACTS),
        decision: finding["decision"] === null ? null : readOneOf(readObject(finding["decision"], "impact decision"), "decision", DECISIONS),
        bulkCarryForwardEligible: readBoolean(finding, "bulk_carry_forward_eligible"),
      };
    }),
  };
}

export function getImpactReport(id: string, signal?: AbortSignal): Promise<ImpactReport> {
  return corpusReviewRequest({ method: "GET", path: `/content/corpus-change-sets/${encodeURIComponent(id)}/impact`, signal, decodeResponse: readImpactReport });
}

export function carryForwardImpact(id: string): Promise<number> {
  return corpusReviewRequest({ method: "POST", path: `/content/corpus-change-sets/${encodeURIComponent(id)}/impact/carry-forward`, decodeResponse: (body) => readArray(body, "impact decisions", (value) => readString(readObject(value, "impact decision"), "identifier")).length });
}
